import * as T from './reservationReportConstants';
import type {
  ReservationSummaryResponse,
  ReservationCheckinKpiResponse,
  ReservationLogResponse,
  ReservationDetailResponse,
  ReservationByStatusItem,
  ReservationHourlyItem,
  TodayNewDetailsResponse,
} from '../../../interface/reservationReportInterface';

export interface ReservationLogRequestParams {
  locationId: string;
  page: number;
  size: number;
  sortBy: string;
  sortDir: string;
  search?: string;
  status?: string;
  date?: string;
}

// ── Reservation Summary ─────────────────────────────────────────────────────

export const fetchReservationSummaryRequest = (locationId: string, date?: string) => ({
  type: T.FETCH_RESERVATION_SUMMARY_REQUEST as typeof T.FETCH_RESERVATION_SUMMARY_REQUEST,
  payload: { locationId, date },
});

export const fetchReservationSummarySuccess = (data: ReservationSummaryResponse) => ({
  type: T.FETCH_RESERVATION_SUMMARY_SUCCESS as typeof T.FETCH_RESERVATION_SUMMARY_SUCCESS,
  payload: data,
});

export const fetchReservationSummaryFailure = (error: string) => ({
  type: T.FETCH_RESERVATION_SUMMARY_FAILURE as typeof T.FETCH_RESERVATION_SUMMARY_FAILURE,
  payload: { error },
});

// ── Checkin KPI ─────────────────────────────────────────────────────────────

export const fetchReservationCheckinKpiRequest = (locationId: string, date?: string) => ({
  type: T.FETCH_RESERVATION_CHECKIN_KPI_REQUEST as typeof T.FETCH_RESERVATION_CHECKIN_KPI_REQUEST,
  payload: { locationId, date },
});

export const fetchReservationCheckinKpiSuccess = (data: ReservationCheckinKpiResponse) => ({
  type: T.FETCH_RESERVATION_CHECKIN_KPI_SUCCESS as typeof T.FETCH_RESERVATION_CHECKIN_KPI_SUCCESS,
  payload: data,
});

export const fetchReservationCheckinKpiFailure = (error: string) => ({
  type: T.FETCH_RESERVATION_CHECKIN_KPI_FAILURE as typeof T.FETCH_RESERVATION_CHECKIN_KPI_FAILURE,
  payload: { error },
});

// ── Reservation Log ─────────────────────────────────────────────────────────

export const fetchReservationLogRequest = (params: ReservationLogRequestParams) => ({
  type: T.FETCH_RESERVATION_LOG_REQUEST as typeof T.FETCH_RESERVATION_LOG_REQUEST,
  payload: params,
});

export const fetchReservationLogSuccess = (data: ReservationLogResponse) => ({
  type: T.FETCH_RESERVATION_LOG_SUCCESS as typeof T.FETCH_RESERVATION_LOG_SUCCESS,
  payload: data,
});

export const fetchReservationLogFailure = (error: string) => ({
  type: T.FETCH_RESERVATION_LOG_FAILURE as typeof T.FETCH_RESERVATION_LOG_FAILURE,
  payload: { error },
});

// ── Reservation Detail ──────────────────────────────────────────────────────

export const fetchReservationDetailRequest = (params: { locationId: string; reservationId: string }) => ({
  type: T.FETCH_RESERVATION_DETAIL_REQUEST as typeof T.FETCH_RESERVATION_DETAIL_REQUEST,
  payload: params,
});

export const fetchReservationDetailSuccess = (data: ReservationDetailResponse) => ({
  type: T.FETCH_RESERVATION_DETAIL_SUCCESS as typeof T.FETCH_RESERVATION_DETAIL_SUCCESS,
  payload: data,
});

export const fetchReservationDetailFailure = (error: string) => ({
  type: T.FETCH_RESERVATION_DETAIL_FAILURE as typeof T.FETCH_RESERVATION_DETAIL_FAILURE,
  payload: { error },
});

export const clearReservationDetail = () => ({
  type: T.CLEAR_RESERVATION_DETAIL as typeof T.CLEAR_RESERVATION_DETAIL,
});

// ── Reservation By Status ───────────────────────────────────────────────────

export const fetchReservationByStatusRequest = (locationId: string, date?: string) => ({
  type: T.FETCH_RESERVATION_BY_STATUS_REQUEST as typeof T.FETCH_RESERVATION_BY_STATUS_REQUEST,
  payload: { locationId, date },
});

export const fetchReservationByStatusSuccess = (data: ReservationByStatusItem[]) => ({
  type: T.FETCH_RESERVATION_BY_STATUS_SUCCESS as typeof T.FETCH_RESERVATION_BY_STATUS_SUCCESS,
  payload: data,
});

export const fetchReservationByStatusFailure = (error: string) => ({
  type: T.FETCH_RESERVATION_BY_STATUS_FAILURE as typeof T.FETCH_RESERVATION_BY_STATUS_FAILURE,
  payload: { error },
});

// ── Hourly Reservations ─────────────────────────────────────────────────────

export const fetchReservationHourlyRequest = (locationId: string, date?: string) => ({
  type: T.FETCH_RESERVATION_HOURLY_REQUEST as typeof T.FETCH_RESERVATION_HOURLY_REQUEST,
  payload: { locationId, date },
});

export const fetchReservationHourlySuccess = (data: ReservationHourlyItem[]) => ({
  type: T.FETCH_RESERVATION_HOURLY_SUCCESS as typeof T.FETCH_RESERVATION_HOURLY_SUCCESS,
  payload: data,
});

export const fetchReservationHourlyFailure = (error: string) => ({
  type: T.FETCH_RESERVATION_HOURLY_FAILURE as typeof T.FETCH_RESERVATION_HOURLY_FAILURE,
  payload: { error },
});

// ── Today New Details ───────────────────────────────────────────────────────

export interface TodayNewDetailsRequestParams {
  locationId: string;
  startDate: string;
  endDate: string;
  page: number;
  size: number;
  search?: string;
  sortBy?: string;
  sortAsc?: boolean;
}

export const fetchTodayNewDetailsRequest = (params: TodayNewDetailsRequestParams) => ({
  type: T.FETCH_TODAY_NEW_DETAILS_REQUEST as typeof T.FETCH_TODAY_NEW_DETAILS_REQUEST,
  payload: params,
});

export const fetchTodayNewDetailsSuccess = (data: TodayNewDetailsResponse) => ({
  type: T.FETCH_TODAY_NEW_DETAILS_SUCCESS as typeof T.FETCH_TODAY_NEW_DETAILS_SUCCESS,
  payload: data,
});

export const fetchTodayNewDetailsFailure = (error: string) => ({
  type: T.FETCH_TODAY_NEW_DETAILS_FAILURE as typeof T.FETCH_TODAY_NEW_DETAILS_FAILURE,
  payload: { error },
});

export type ReservationReportAction =
  | ReturnType<typeof fetchReservationSummaryRequest>
  | ReturnType<typeof fetchReservationSummarySuccess>
  | ReturnType<typeof fetchReservationSummaryFailure>
  | ReturnType<typeof fetchReservationCheckinKpiRequest>
  | ReturnType<typeof fetchReservationCheckinKpiSuccess>
  | ReturnType<typeof fetchReservationCheckinKpiFailure>
  | ReturnType<typeof fetchReservationLogRequest>
  | ReturnType<typeof fetchReservationLogSuccess>
  | ReturnType<typeof fetchReservationLogFailure>
  | ReturnType<typeof fetchReservationDetailRequest>
  | ReturnType<typeof fetchReservationDetailSuccess>
  | ReturnType<typeof fetchReservationDetailFailure>
  | ReturnType<typeof clearReservationDetail>
  | ReturnType<typeof fetchReservationByStatusRequest>
  | ReturnType<typeof fetchReservationByStatusSuccess>
  | ReturnType<typeof fetchReservationByStatusFailure>
  | ReturnType<typeof fetchReservationHourlyRequest>
  | ReturnType<typeof fetchReservationHourlySuccess>
  | ReturnType<typeof fetchReservationHourlyFailure>
  | ReturnType<typeof fetchTodayNewDetailsRequest>
  | ReturnType<typeof fetchTodayNewDetailsSuccess>
  | ReturnType<typeof fetchTodayNewDetailsFailure>;
